import type { Locale, Translations } from '@/shared/contexts/TranslationContextTypes'
import { getDictionarySync, i18nLoader, i18nKeys } from '@/lib/i18n/getDictionary'

export function deepMerge(target: any, source: any) {
  for (const key of Object.keys(source || {})) {
    const srcVal = source[key]
    const tgtVal = target[key]

    if (Array.isArray(srcVal)) {
      target[key] = srcVal.slice()
    } else if (srcVal && typeof srcVal === 'object') {
      if (!tgtVal || typeof tgtVal !== 'object') target[key] = {}
      deepMerge(target[key], srcVal)
    } else {
      target[key] = srcVal
    }
  }
}

export function buildTranslationsSync(locale: Locale, unitContext: string): Translations {
  const common: any = {}

  const baseCommonPath = `./${locale}/common.json`
  const baseCommon = i18nLoader?.(baseCommonPath)

  if (baseCommon) {
    deepMerge(common, baseCommon.default ?? baseCommon)
  }

  if (unitContext) {
    const contextPath = `./${locale}/${unitContext}.json`

    if (i18nKeys.includes(contextPath)) {
      const contextData = i18nLoader?.(contextPath)

      if (contextData) {
        deepMerge(common, contextData.default ?? contextData)
      }
    }
  }

  const dashboard = getDictionarySync(locale, 'dashboard')
  if (dashboard && Object.keys(dashboard).length > 0) {
    deepMerge(common, { dashboard })
  }

  const onboarding = getDictionarySync(locale, 'onboarding')
  if (onboarding && Object.keys(onboarding).length > 0) {
    deepMerge(common, { onboarding })
  }

  return {
    common,
    units: getDictionarySync(locale, 'units'),
    assets: getDictionarySync(locale, 'assets'),
    settings: getDictionarySync(locale, 'settings'),
    billing: getDictionarySync(locale, 'billing'),
    registrations: getDictionarySync(locale, 'registrations'),
  }
}

export function traverseKeys(obj: any, keyPath: string): any {
  const parts = keyPath.split('.')
  let value = obj

  for (const part of parts) {
    if (value && typeof value === 'object' && part in value) {
      value = value[part]
    } else {
      return undefined
    }
  }

  return value
}

export function findValue(obj: any, keys: string[]): any {
  let value = obj

  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key]
    } else {
      return undefined
    }
  }

  return value
}

export function interpolate(value: string, params?: Record<string, string>): string {
  if (!params) return value

  let result = value

  Object.entries(params).forEach(([param, replacement]) => {
    result = result.replace(new RegExp(`{{${param}}}`, 'g'), String(replacement))
  })

  return result
}